import { useRef } from 'react';

import { Box, Card, CardContent, Typography } from '@mui/material';

import DownArrowIcon from '@/assets/images/downRedCrackedArrow.svg';
import UpArrowIcon from '@/assets/images/upGreenCrackedArrow.svg';
import ElectricBorder from '@/features/Dashboard/components/ElectricBorder/ElectricBorder';
import { useScrollToAnimatedElement } from '@/features/Dashboard/context/WidgetAnimation/hooks';
import FactsheetDialogButton from '@/features/Factsheet/components/FactsheetDialogButton';
import type { TileType } from '@/pages/Markets/MarketsComponents/Tiles/types';
import { getDiffColor } from '@/utils/getDiffColor';

const Tile = ({ tile }: { tile: TileType }) => {
  const { code, shortTitle, value, delta, deltaPercentage } = tile;
  const ref = useRef<HTMLDivElement>(null);
  const isAnimating = useScrollToAnimatedElement(ref, tile.id);
  const priceColor = getDiffColor(Number(delta));

  return (
    <ElectricBorder animate={isAnimating}>
      <Card
        ref={ref}
        sx={{
          display: 'flex',
          flexGrow: 1,
          borderLeft: `4px solid`,
          borderColor: priceColor,
          lineHeight: 1
        }}
      >
        <CardContent
          sx={{
            width: '100%',
            p: '6px 8px 6px 4px',
            '&:last-child': {
              pb: '2px'
            }
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography
              noWrap
              component='div'
              sx={{ fontSize: '24px', fontWeight: 600, lineHeight: '1' }}
            >
              {code || 'GXCODE'}
            </Typography>
            <Typography
              component='div'
              sx={{ fontSize: '24px', fontWeight: 600, lineHeight: '1' }}
            >
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography
              noWrap
              component='div'
              fontSize='14px'
              mr={1}
              position='relative'
              pr='18px'
            >
              {shortTitle}
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  right: 0,
                  transform: 'translateY(-50%)'
                }}
              >
                <FactsheetDialogButton
                  showTooltip
                  code={code}
                  fontSize={14}
                />
              </Box>
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'nowrap',

                justifyContent: 'flex-end'
              }}
            >
              {!!delta && Number(delta) !== 0 && (
                <Box
                  alt={String(delta).startsWith('-') ? 'Down' : 'Up'}
                  component='img'
                  src={String(delta).startsWith('-') ? DownArrowIcon : UpArrowIcon}
                  sx={{ fontSize: 12, marginRight: 1 }}
                />
              )}
              <Typography>{delta}&nbsp;</Typography>
              <Typography>({deltaPercentage}%)</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </ElectricBorder>
  );
};

export default Tile;
